import {
  convertValue,
  getDecimalPoint,
  MEASUREMENT_KEYS,
  GlobalUnitEnum,
} from "../unit-conversion";

const mapZoneViewColumnTitles = (inputColumns, reportColumns) => {
  const columnsMapping = {
    CURRENT_A: "currentA",
    VOLT_V: "voltV",
    TRAVEL_SPEED: "travelSpeed",
    TRUE_ENERGY: "trueEnergy",
    HEAT: "heat",
    WIRE_SPEED: "wireSpeed",
    OSCILLATION_WIDTH: "oscillationWidth",
    FREQUENCY: "frequency",
    TARGET: "target",
    HORIZONTAL_BIAS: "horizontalBias",
    TOTAL_WIRE_CONSUMED: "totalWireConsumed",
    ARC_IMPEDANCE: "arcImpedance",
  };

  return inputColumns?.map((ite) => {
    let changeKeys = [
      columnsMapping.CURRENT_A,
      columnsMapping.VOLT_V,
      columnsMapping.TRAVEL_SPEED,
      columnsMapping.TRUE_ENERGY,
      columnsMapping.HEAT,
      columnsMapping.WIRE_SPEED,
      columnsMapping.OSCILLATION_WIDTH,
      columnsMapping.FREQUENCY,
      columnsMapping.TARGET,
      columnsMapping.HORIZONTAL_BIAS,
      columnsMapping.TOTAL_WIRE_CONSUMED,
      columnsMapping.ARC_IMPEDANCE,
    ];

    if (changeKeys.includes(ite.key)) {
      switch (ite.key) {
        case columnsMapping.CURRENT_A: {
          return {
            ...ite,
            title: `Current (${reportColumns.units.ArcCurrent || "A"})`,
            headerName: `Current (${reportColumns.units.ArcCurrent || "A"})`,
          };
        }
        case columnsMapping.VOLT_V: {
          return {
            ...ite,
            title: `Volt (${reportColumns.units.ArcVolts || "V"})`,
            headerName: `Volt (${reportColumns.units.ArcVolts || "V"})`,
          };
        }

        case columnsMapping.TRAVEL_SPEED: {
          return {
            ...ite,
            title: `Travel Speed (${
              reportColumns.units["Travel Speed"] || "in/min"
            })`,
            headerName: `Travel Speed (${
              reportColumns.units["Travel Speed"] || "in/min"
            })`,
          };
        }

        case columnsMapping.TRUE_ENERGY: {
          return {
            ...ite,
            title: `True Energy (${
              reportColumns.units["True Energy"] || "KJ/in"
            })`,
            headerName: `True Energy (${
              reportColumns.units["True Energy"] || "KJ/in"
            })`,
          };
        }

        case columnsMapping.HEAT: {
          return {
            ...ite,
            title: `Heat (${reportColumns.units.Heat || "KJ/in"})`,
            headerName: `Heat (${reportColumns.units.Heat || "KJ/in"})`,
          };
        }

        case columnsMapping.WIRE_SPEED: {
          return {
            ...ite,
            title: `Wire Speed (${
              reportColumns.units["Wire Speed"] || "in/min"
            })`,
            headerName: `Wire Speed (${
              reportColumns.units["Wire Speed"] || "in/min"
            })`,
          };
        }

        case columnsMapping.OSCILLATION_WIDTH: {
          return {
            ...ite,
            title: `Oscillation Width (${
              reportColumns.units["Oscillation Width"] || "in"
            })`,
            headerName: `Oscillation Width (${
              reportColumns.units["Oscillation Width"] || "in"
            })`,
          };
        }

        case columnsMapping.FREQUENCY: {
          return {
            ...ite,
            title: `Frequency (${reportColumns.units.Frequency || "cyc/min"})`,
            headerName: `Frequency (${
              reportColumns.units.Frequency || "cyc/min"
            })`,
          };
        }

        case columnsMapping.TARGET: {
          return {
            ...ite,
            title: `Target (${reportColumns.units.Target || "A"})`,
            headerName: `Target (${reportColumns.units.Target || "A"})`,
          };
        }

        case columnsMapping.HORIZONTAL_BIAS: {
          return {
            ...ite,
            title: `Horizontal Bias (${
              reportColumns.units["Horizontal Bias"] || "A"
            })`,
            headerName: `Horizontal Bias (${
              reportColumns.units["Horizontal Bias"] || "A"
            })`,
          };
        }

        case columnsMapping.TOTAL_WIRE_CONSUMED: {
          return {
            ...ite,
            title: `Total Wire Consumed (${
              reportColumns.units["Total Wire Consumed"] || "in"
            })`,
            headerName: `Total Wire Consumed (${
              reportColumns.units["Total Wire Consumed"] || "in"
            })`,
          };
        }

        case columnsMapping.ARC_IMPEDANCE:
          return {
            ...ite,
            title: `Arc Impedance (${
              reportColumns.units["Arc Impedance"] || "mOhm"
            })`,
            headerName: `Arc Impedance (${
              reportColumns.units["Arc Impedance"] || "mOhm"
            })`,
          };
      }
    } else {
      return ite;
    }
  });
};

const zoneViewSorting = (avgList) => {
  let zoneDataObject = {};

  avgList.map((dataItem) => {
    let this_s_deviceid_event = dataItem.s_deviceid_event;
    if (!zoneDataObject[this_s_deviceid_event]) {
      zoneDataObject[this_s_deviceid_event] = [];
      zoneDataObject[this_s_deviceid_event].push(dataItem);
    } else {
      let arrTempDataItem = zoneDataObject[this_s_deviceid_event];
      arrTempDataItem.push(dataItem);
      zoneDataObject[this_s_deviceid_event] = arrTempDataItem;
    }
  });

  let sortedData = [];

  for (let sdeviceIdEvent of Object.keys(zoneDataObject)) {
    let arrTemp = zoneDataObject[sdeviceIdEvent];
    arrTemp?.sort((a, b) => {
      return Number(1000 * b.s_c_time) < Number(1000 * a.s_c_time);
    });

    for (let i = 0; i < arrTemp?.length; i++) {
      if (arrTemp[i]?.lead_pass_name) {
        sortedData.push(arrTemp[i]);
      }
    }
    for (let i = 0; i < arrTemp?.length; i++) {
      if (arrTemp[i]?.trail_pass_name) {
        sortedData.push(arrTemp[i]);
      }
    }
  }

  return sortedData;
};

const mapperForUnitConversionZoneView = (
  inputItemObject,
  reportColumns,
  globalUnit = GlobalUnitEnum.IMPERIAL
) => {
  //current
  inputItemObject.current_low = convertValue(
    inputItemObject.current_low,
    reportColumns.units.ArcCurrent,
    false,
    getDecimalPoint(MEASUREMENT_KEYS.AMPS, globalUnit)
  );
  inputItemObject.current_avg = convertValue(
    inputItemObject.current_avg,
    reportColumns.units.ArcCurrent,
    false,
    getDecimalPoint(MEASUREMENT_KEYS.AMPS, globalUnit)
  );
  inputItemObject.current_high = convertValue(
    inputItemObject.current_high,
    reportColumns.units.ArcCurrent,
    false,
    getDecimalPoint(MEASUREMENT_KEYS.AMPS, globalUnit)
  );

  //volt
  inputItemObject.volt_low = convertValue(
    inputItemObject.volt_low,
    reportColumns.units.ArcVolts,
    false,
    getDecimalPoint(MEASUREMENT_KEYS.VOLTS, globalUnit)
  );
  inputItemObject.volt_avg = convertValue(
    inputItemObject.volt_avg,
    reportColumns.units.ArcVolts,
    false,
    getDecimalPoint(MEASUREMENT_KEYS.VOLTS, globalUnit)
  );
  inputItemObject.volt_high = convertValue(
    inputItemObject.volt_high,
    reportColumns.units.ArcVolts,
    false,
    getDecimalPoint(MEASUREMENT_KEYS.VOLTS, globalUnit)
  );

  //travel speed
  inputItemObject.travel_speed_low = convertValue(
    inputItemObject.travel_speed_low,
    reportColumns.units["Travel Speed"],
    false,
    getDecimalPoint(MEASUREMENT_KEYS.TRAVEL_SPEED, globalUnit)
  );
  inputItemObject.travel_speed_avg = convertValue(
    inputItemObject.travel_speed_avg,
    reportColumns.units["Travel Speed"],
    false,
    getDecimalPoint(MEASUREMENT_KEYS.TRAVEL_SPEED, globalUnit)
  );
  inputItemObject.travel_speed_high = convertValue(
    inputItemObject.travel_speed_high,
    reportColumns.units["Travel Speed"],
    false,
    getDecimalPoint(MEASUREMENT_KEYS.TRAVEL_SPEED, globalUnit)
  );

  //heat(calculated)
  inputItemObject.heat_low = convertValue(
    inputItemObject.heat_low,
    reportColumns.units.Heat,
    false,
    getDecimalPoint(MEASUREMENT_KEYS.HEAT, globalUnit)
  );
  inputItemObject.heat_avg = convertValue(
    inputItemObject.heat_avg,
    reportColumns.units.Heat,
    false,
    getDecimalPoint(MEASUREMENT_KEYS.HEAT, globalUnit)
  );
  inputItemObject.heat_high = convertValue(
    inputItemObject.heat_high,
    reportColumns.units.Heat,
    false,
    getDecimalPoint(MEASUREMENT_KEYS.HEAT, globalUnit)
  );

  //true energy
  inputItemObject.true_energy_low = convertValue(
    inputItemObject.true_energy_low,
    reportColumns.units["True Energy"],
    false,
    getDecimalPoint(MEASUREMENT_KEYS.TRUE_ENERGY, globalUnit)
  );
  inputItemObject.true_energy_avg = convertValue(
    inputItemObject.true_energy_avg,
    reportColumns.units["True Energy"],
    false,
    getDecimalPoint(MEASUREMENT_KEYS.TRUE_ENERGY, globalUnit)
  );
  inputItemObject.true_energy_high = convertValue(
    inputItemObject.true_energy_high,
    reportColumns.units["True Energy"],
    false,
    getDecimalPoint(MEASUREMENT_KEYS.TRUE_ENERGY, globalUnit)
  );

  //wire speed
  inputItemObject.wire_speed_low = convertValue(
    inputItemObject.wire_speed_low,
    reportColumns.units["Wire Speed"],
    false,
    getDecimalPoint(MEASUREMENT_KEYS.WIRE_SPEED, globalUnit)
  );
  inputItemObject.wire_speed_avg = convertValue(
    inputItemObject.wire_speed_avg,
    reportColumns.units["Wire Speed"],
    false,
    getDecimalPoint(MEASUREMENT_KEYS.WIRE_SPEED, globalUnit)
  );
  inputItemObject.wire_speed_high = convertValue(
    inputItemObject.wire_speed_high,
    reportColumns.units["Wire Speed"],
    false,
    getDecimalPoint(MEASUREMENT_KEYS.WIRE_SPEED, globalUnit)
  );

  //oscillation width
  inputItemObject.oscillation_width_low = convertValue(
    inputItemObject.oscillation_width_low,
    reportColumns.units["Oscillation Width"],
    false,
    getDecimalPoint(MEASUREMENT_KEYS.OSCILLATE_WIDTH, globalUnit)
  );
  inputItemObject.oscillation_width_avg = convertValue(
    inputItemObject.oscillation_width_avg,
    reportColumns.units["Oscillation Width"],
    false,
    getDecimalPoint(MEASUREMENT_KEYS.OSCILLATE_WIDTH, globalUnit)
  );
  inputItemObject.oscillation_width_high = convertValue(
    inputItemObject.oscillation_width_high,
    reportColumns.units["Oscillation Width"],
    false,
    getDecimalPoint(MEASUREMENT_KEYS.OSCILLATE_WIDTH, globalUnit)
  );

  //frequency
  inputItemObject.frequency_low = convertValue(
    inputItemObject.frequency_low,
    reportColumns.units.Frequency,
    false,
    getDecimalPoint(MEASUREMENT_KEYS.FREQUENCY, globalUnit)
  );
  inputItemObject.frequency_avg = convertValue(
    inputItemObject.frequency_avg,
    reportColumns.units.Frequency,
    false,
    getDecimalPoint(MEASUREMENT_KEYS.FREQUENCY, globalUnit)
  );
  inputItemObject.frequency_high = convertValue(
    inputItemObject.frequency_high,
    reportColumns.units.Frequency,
    false,
    getDecimalPoint(MEASUREMENT_KEYS.FREQUENCY, globalUnit)
  );

  //target
  inputItemObject.target_low = convertValue(
    inputItemObject.target_low,
    reportColumns.units.Target,
    false,
    getDecimalPoint(MEASUREMENT_KEYS.TARGET, globalUnit)
  );
  inputItemObject.target_avg = convertValue(
    inputItemObject.target_avg,
    reportColumns.units.Target,
    false,
    getDecimalPoint(MEASUREMENT_KEYS.TARGET, globalUnit)
  );
  inputItemObject.target_high = convertValue(
    inputItemObject.target_high,
    reportColumns.units.Target,
    false,
    getDecimalPoint(MEASUREMENT_KEYS.TARGET, globalUnit)
  );

  //horizontal bias
  inputItemObject.horizontal_bias_low = convertValue(
    inputItemObject.horizontal_bias_low,
    reportColumns.units["Horizontal Bias"],
    false,
    getDecimalPoint(MEASUREMENT_KEYS.HORIZONTAL_BIAS, globalUnit)
  );
  inputItemObject.horizontal_bias_avg = convertValue(
    inputItemObject.horizontal_bias_avg,
    reportColumns.units["Horizontal Bias"],
    false,
    getDecimalPoint(MEASUREMENT_KEYS.HORIZONTAL_BIAS, globalUnit)
  );
  inputItemObject.horizontal_bias_high = convertValue(
    inputItemObject.horizontal_bias_high,
    reportColumns.units["Horizontal Bias"],
    false,
    getDecimalPoint(MEASUREMENT_KEYS.HORIZONTAL_BIAS, globalUnit)
  );

  //total wire consumed
  inputItemObject.total_wire_consumed_low = convertValue(
    inputItemObject.total_wire_consumed_low,
    reportColumns.units["Total Wire Consumed"],
    false,
    getDecimalPoint(MEASUREMENT_KEYS.TOTAL_WIRE_CONSUMED, globalUnit)
  );
  inputItemObject.total_wire_consumed_avg = convertValue(
    inputItemObject.total_wire_consumed_avg,
    reportColumns.units["Total Wire Consumed"],
    false,
    getDecimalPoint(MEASUREMENT_KEYS.TOTAL_WIRE_CONSUMED, globalUnit)
  );
  inputItemObject.total_wire_consumed_high = convertValue(
    inputItemObject.total_wire_consumed_high,
    reportColumns.units["Total Wire Consumed"],
    false,
    getDecimalPoint(MEASUREMENT_KEYS.TOTAL_WIRE_CONSUMED, globalUnit)
  );

  return inputItemObject;
};

export const zoneViewHelper = {
  mapZoneViewColumnTitles,
  mapperForUnitConversionZoneView,
  zoneViewSorting,
};
